import {
    DataTable,
    DateField,
    EditButton,
    NumberField,
    ReferenceManyField,
    Show,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
    AlertTriangle,
    ArrowDownUp,
    ArrowLeft,
    Barcode,
    CircleDollarSign,
    Layers,
    Package,
} from 'lucide-react';
import { useGetList, useRecordContext } from 'ra-core';
import { Link } from 'react-router-dom';

const HSNShowField = () => {
    const record = useRecordContext();
    const hsnCode = record?.hsn_code;

    const { data: hsnData, isPending } = useGetList('hsn_codes', {
        pagination: { page: 1, perPage: 1 },
        filter: { code: hsnCode }
    }, { enabled: !!hsnCode });

    if (!hsnCode) return <span className="text-slate-400">—</span>;

    const gstRate = hsnData?.[0]?.gst_rate;

    return (
        <div className="flex items-center gap-2">
            <span className="font-mono">{hsnCode}</span>
            {gstRate !== undefined && !isPending && (
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                    {gstRate}% GST
                </Badge>
            )}
        </div>
    );
};

const ShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/inventory_items" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4" /> Back to Inventory
                </Link>
            </Button>
            <span className="text-xl font-bold tracking-tight text-slate-900 ml-1">{record.name}</span>
        </div>
    );
};

const InventoryShowActions = () => (
    <div className="flex items-center gap-2">
        <EditButton />
    </div>
);

const InventoryShowContent = () => {
    const record = useRecordContext();
    if (!record) return null;

    const stock = record.current_stock ?? 0;
    const min = record.min_stock_level ?? 0;
    const isLow = stock <= min && min > 0;
    const stockPct = min > 0 ? Math.min(100, Math.round((stock / (min * 3)) * 100)) : 50;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8">
            {/* Header Banner */}
            <div className="flex justify-between items-start border-b pb-8">
                <div className="flex gap-6 items-center">
                    <div className={`w-20 h-20 rounded-xl flex items-center justify-center border-2 shadow-sm ${isLow ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                        <Package className={`w-10 h-10 ${isLow ? 'text-red-400' : 'text-blue-400'}`} />
                    </div>
                    <div className="space-y-1">
                        <div className="flex items-center gap-3">
                            <h2 className="text-3xl font-bold tracking-tight text-slate-900">{record.name}</h2>
                            {isLow && (
                                <Badge className="bg-red-100 text-red-700 border-red-300 text-xs flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> Low Stock
                                </Badge>
                            )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span className="font-mono bg-slate-100 px-2 py-0.5 rounded border text-xs font-bold">
                                SKU: {record.sku}
                            </span>
                            {record.category && (
                                <>
                                    <span className="text-slate-300">|</span>
                                    <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-xs">
                                        <Layers className="w-3 h-3 mr-1" />
                                        {record.category}
                                    </Badge>
                                </>
                            )}
                            {record.hsn_code && (
                                <>
                                    <span className="text-slate-300">|</span>
                                    <span className="font-mono text-xs flex items-center gap-1">
                                        <Barcode className="w-3 h-3" /> HSN: {record.hsn_code}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Main Details */}
                <div className="md:col-span-2 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader className="pb-3 border-b">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <Package className="w-3.5 h-3.5" /> Stock Levels
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 space-y-6">
                                <div className="space-y-2">
                                    <div className="flex justify-between items-end">
                                        <span className="text-xs font-bold text-slate-500 uppercase">Current Stock</span>
                                        <span className={`text-3xl font-black ${isLow ? 'text-red-600' : 'text-slate-900'}`}>
                                            {stock}
                                        </span>
                                    </div>
                                    <Progress value={stockPct} className="h-2" />
                                    <div className={`h-1.5 rounded-full mt-1 transition-all duration-500 ${isLow ? 'bg-red-500' : stockPct > 60 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${stockPct}%` }} />
                                </div>

                                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-dashed">
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase">Min Level</span>
                                        <div className="text-lg font-bold text-slate-900">{min}</div>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase">Unit</span>
                                        <div className="text-lg font-bold text-slate-900">{record.unit || 'Nos'}</div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3 border-b">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <Barcode className="w-3.5 h-3.5" /> Item Identity
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 space-y-4">
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase">Item Name</span>
                                    <div className="text-sm font-semibold text-slate-900">
                                        <TextField source="name" />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase">Category</span>
                                    <div className="text-sm font-semibold text-slate-900">
                                        <TextField source="category" emptyText="Uncategorized" />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase">HSN Code</span>
                                    <div className="text-sm font-bold text-slate-900">
                                        <HSNShowField />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Transaction History */}
                    <Card>
                        <CardHeader className="pb-3 border-b bg-slate-50/50">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                <ArrowDownUp className="w-4 h-4" /> Transaction History
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <ReferenceManyField reference="inventory_transactions" target="item_id">
                                <DataTable bulkActionButtons={false}>
                                    <DataTable.Col source="transaction_date" label="Date">
                                        <DateField source="transaction_date" showTime />
                                    </DataTable.Col>
                                    <DataTable.Col source="transaction_type" label="Type" />
                                    <DataTable.Col source="quantity" label="Qty">
                                        <NumberField source="quantity" />
                                    </DataTable.Col>
                                    <DataTable.Col source="reference_type" label="Ref Type" />
                                    <DataTable.Col source="notes" label="Notes" />
                                </DataTable>
                            </ReferenceManyField>
                        </CardContent>
                    </Card>
                </div>

                {/* Financial Sidebar */}
                <div className="space-y-6">
                    <Card className="bg-slate-900 text-white border-none shadow-lg">
                        <CardHeader className="pb-3 border-b border-slate-800">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                <CircleDollarSign className="w-3.5 h-3.5" /> Financial Data
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-6">
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Unit Price</span>
                                <div className="text-2xl font-black">
                                    <NumberField source="unit_price" options={{ style: 'currency', currency: 'INR' }} />
                                </div>
                            </div>

                            <div className="pt-4 border-t border-slate-800 space-y-1">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Total Stock Value</span>
                                <div className="text-lg font-bold text-emerald-400">
                                    {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format((record.unit_price || 0) * stock)}
                                </div>
                                <span className="text-[10px] text-slate-500 italic">
                                    {stock} × {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.unit_price || 0)}
                                </span>
                            </div>
                        </CardContent>
                    </Card>

                    {isLow && (
                        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-700 leading-relaxed">
                            <span className="font-bold not-italic flex items-center gap-1 mb-1">
                                <AlertTriangle className="w-3.5 h-3.5" /> Stock Alert
                            </span>
                            Current stock ({stock} {record.unit}) is at or below the minimum level ({min} {record.unit}). Consider raising a Purchase Order.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const InventoryShow = () => (
    <Show title={<ShowTitle />} actions={<InventoryShowActions />}>
        <InventoryShowContent />
    </Show>
);
